import React from 'react';
import { Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Categoria } from '../../../types/consolidatedReport.types';

interface CategoryBarsChartProps {
  categorias: Categoria[];
}

const CategoryBarsChart: React.FC<CategoryBarsChartProps> = ({ categorias }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sin truncamiento: el YAxis se ajusta dinámicamente al nombre más largo
  const data = categorias.map(cat => ({
    nombre: isMobile
      ? (cat.name.length > 14 ? cat.name.substring(0, 14) + '…' : cat.name)
      : cat.name,
    nombreCompleto: cat.name, // para el tooltip
    promedio: Math.round(cat.average * 10) / 10,
    evaluacion: cat.evaluation
  }));

  // Ancho del eje Y según el nombre más largo (aprox. 7px por carácter en desktop)
  const maxNombreLen = categorias.reduce((max, c) => Math.max(max, c.name.length), 0);
  const yAxisWidth = isMobile ? 90 : Math.min(Math.max(maxNombreLen * 7, 120), 220);

  // Altura dinámica: mínimo 300px, 44px por categoría
  const chartHeight = Math.max(isMobile ? 250 : 300, categorias.length * (isMobile ? 36 : 44));

  // Semáforo unificado: misma escala que dashboard, audits y CategoryComparisonChart
  const getColor = (average: number) => {
    if (average >= 80) return '#43A047'; // verde
    if (average >= 60) return '#FFA000'; // ámbar
    return '#E53935';                   // rojo
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 2 : 3,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
        Promedio por Categoría
      </Typography>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart 
          data={data} 
          layout="vertical" 
          margin={{ 
            left: isMobile ? 10 : 16,
            right: isMobile ? 10 : 20,
            top: 5,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: isMobile ? 10 : 12 }} />
          <YAxis 
            dataKey="nombre" 
            type="category" 
            tick={{ fontSize: isMobile ? 9 : 11 }}
            width={yAxisWidth}
          />
          <Tooltip
            formatter={(value: number) => [`${value.toFixed(1)}%`, 'Promedio']}
            labelFormatter={(label) => {
              const item = data.find(d => d.nombre === label);
              return item?.nombreCompleto ?? label;
            }}
          />
          <Bar dataKey="promedio" radius={[0, 8, 8, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.promedio)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default CategoryBarsChart;
